import React from "react";

const FONT   = "'Inter','Avenir Next','Century Gothic',sans-serif";
const TEAL   = "#4fd1c5";
const BG     = "#0f1117";
const PANEL  = "#161b24";
const BORDER = "#2d3748";
const TEXT   = "#e8e6e1";
const MUTED  = "#708898";

interface PrivacyPolicyPageProps {
  onBack?: () => void;
}

// ── Reusable layout components ───────────────────────────────────────────────

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 style={{
      fontWeight: 700, color: TEAL,
      margin: "40px 0 10px",
      textTransform: "uppercase", letterSpacing: "0.06em", fontSize: 12,
    }}>
      {children}
    </h2>
  );
}

function BodyText({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <p style={{
      fontSize: 14, color: MUTED, lineHeight: 1.75,
      margin: "0 0 14px", ...style,
    }}>
      {children}
    </p>
  );
}

function Term({ children }: { children: React.ReactNode }) {
  return <strong style={{ color: TEXT, fontWeight: 600 }}>{children}</strong>;
}

function Divider() {
  return <div style={{ borderTop: `1px solid ${BORDER}`, margin: "36px 0" }} />;
}

function BulletList({ items }: { items: React.ReactNode[] }) {
  return (
    <ul style={{ margin: "0 0 14px", paddingLeft: 20 }}>
      {items.map((item, i) => (
        <li key={i} style={{ fontSize: 14, color: MUTED, lineHeight: 1.75, marginBottom: 6 }}>
          {item}
        </li>
      ))}
    </ul>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function PrivacyPolicyPage({ onBack }: PrivacyPolicyPageProps) {
  return (
    <div style={{ minHeight: "100vh", background: BG, fontFamily: FONT, color: TEXT }}>

      {/* ── Nav bar ── */}
      <nav style={{
        borderBottom: `1px solid ${BORDER}`, padding: "14px 32px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        position: "sticky", top: 0, background: BG, zIndex: 100,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            onClick={onBack}
            style={{ fontSize: 17, fontWeight: 800, cursor: onBack ? "pointer" : "default", userSelect: "none" }}
            title={onBack ? "Home" : undefined}
          >
            <span style={{ color: TEXT }}>Open</span>
            <span style={{ color: TEAL }}>Estimate</span>
          </span>
          <span style={{ color: BORDER, fontSize: 16, margin: "0 4px" }}>/</span>
          <span style={{ fontSize: 13, color: MUTED, fontWeight: 500 }}>Privacy Policy</span>
        </div>
        {onBack && (
          <button onClick={onBack} style={{
            background: "none", border: `1px solid ${BORDER}`, color: MUTED,
            borderRadius: 6, padding: "6px 16px", fontSize: 13, cursor: "pointer",
            fontFamily: FONT,
          }}>
            ← Back
          </button>
        )}
      </nav>

      {/* ── Body ── */}
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "56px 32px 80px" }}>

        {/* Hero */}
        <div style={{ marginBottom: 48 }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            background: TEAL + "15", border: `1px solid ${TEAL}30`,
            borderRadius: 999, padding: "6px 16px", marginBottom: 24,
          }}>
            <span style={{ fontSize: 12, color: TEAL, fontWeight: 600, letterSpacing: "0.06em" }}>
              PRIVACY POLICY
            </span>
          </div>
          <h1 style={{
            fontSize: 36, fontWeight: 800, letterSpacing: "-0.03em",
            margin: "0 0 16px", lineHeight: 1.15, color: TEXT,
          }}>
            Your privacy matters.<br />
            <span style={{ color: TEAL }}>Here's exactly what we do with your data.</span>
          </h1>
          <p style={{ fontSize: 14, color: MUTED, lineHeight: 1.7, maxWidth: 600, margin: 0 }}>
            This Privacy Policy explains how <Term>OpenEstimate</Term>, operated by{" "}
            <Term>Alex Bruce</Term>, collects, uses, stores, and shares your information
            when you use our construction cost estimation platform at{" "}
            <a href="https://open-estimate.com" style={{ color: TEAL, textDecoration: "none" }}>
              open-estimate.com
            </a>.
          </p>
          <p style={{ fontSize: 13, color: MUTED + "99", marginTop: 16, marginBottom: 0 }}>
            Last updated: <Term>April 2026</Term>
          </p>
        </div>

        <div style={{
          background: PANEL, border: `1px solid ${BORDER}`,
          borderRadius: 10, padding: "20px 24px", marginBottom: 40,
        }}>
          <p style={{ fontSize: 13, color: MUTED, lineHeight: 1.7, margin: 0 }}>
            <Term>Summary:</Term> We collect your email, name, and project data to provide the service.
            We don't sell your data. We use SOC 2-certified vendors. You can export or permanently
            delete all your data at any time from{" "}
            <Term>Settings → Privacy &amp; Data</Term>.
          </p>
        </div>

        {/* 1. Who We Are */}
        <SectionHeading>1. Who We Are</SectionHeading>
        <BodyText>
          <Term>OpenEstimate</Term> is a cloud-based pre-construction cost estimation
          platform operated by Alex Bruce as a sole proprietor. References to "we," "us," or "our"
          in this policy refer to OpenEstimate. You can contact us at{" "}
          <a href="mailto:privacy@open-estimate.com" style={{ color: TEAL, textDecoration: "none" }}>
            privacy@open-estimate.com
          </a>.
        </BodyText>

        <Divider />

        {/* 2. Data We Collect */}
        <SectionHeading>2. Data We Collect</SectionHeading>
        <BodyText>
          We collect only the data necessary to provide and improve the service:
        </BodyText>
        <BulletList items={[
          <><Term>Account data:</Term> Your email address and display name, provided when you register or sign in via Google OAuth.</>,
          <><Term>Project data:</Term> All estimate content you create — divisions, line items, quantities, unit costs, schedules, bid data, snapshots, and notes. This data belongs to you.</>,
          <><Term>Usage metadata:</Term> Feature interactions, page views, and performance metrics collected in aggregate to improve the product. We do not build individual behavioral profiles for advertising.</>,
          <><Term>IP address:</Term> Logged server-side for security audit purposes (e.g. detecting unauthorized access). Retained for 90 days and not used for any other purpose.</>,
          <><Term>Subscription and billing data:</Term> Managed entirely by Stripe. We receive only a Stripe customer ID and subscription status — we never see or store your full card number.</>,
        ]} />

        <Divider />

        {/* 3. How We Use Your Data */}
        <SectionHeading>3. How We Use Your Data</SectionHeading>
        <BulletList items={[
          <><Term>To provide the service:</Term> Store and serve your projects, authenticate your identity, and compute estimate totals.</>,
          <><Term>Transactional email:</Term> Send account confirmations, password resets, and subscription receipts. We do not send marketing emails without your explicit opt-in.</>,
          <><Term>Security and fraud prevention:</Term> Monitor for unauthorized access patterns using IP and session logs.</>,
          <><Term>Product improvement:</Term> Analyze aggregated, de-identified usage patterns to prioritize features. We do not share individual usage data externally.</>,
        ]} />

        <Divider />

        {/* 4. Legal Basis (GDPR) */}
        <SectionHeading>4. Legal Basis for Processing (GDPR)</SectionHeading>
        <BodyText>
          If you are located in the European Economic Area (EEA) or United Kingdom, our legal bases
          for processing your personal data are:
        </BodyText>
        <BulletList items={[
          <><Term>Contract performance (Art. 6(1)(b)):</Term> Processing your account data and project data is necessary to fulfill our contract with you — providing the OpenEstimate service.</>,
          <><Term>Legitimate interests (Art. 6(1)(f)):</Term> Processing IP addresses for security audit logs serves our legitimate interest in protecting the platform and our users from unauthorized access, and is not overridden by your privacy rights.</>,
          <><Term>Consent (Art. 6(1)(a)):</Term> Where required, we obtain your consent before sending optional communications. You may withdraw consent at any time.</>,
        ]} />

        <Divider />

        {/* 5. Data Sharing */}
        <SectionHeading>5. Data Sharing and Third Parties</SectionHeading>
        <BodyText>
          <Term>We do not sell your personal data.</Term> We do not share your project data with
          advertising networks, data brokers, or any party for commercial purposes unrelated to
          providing the service. Your data is shared only with the following sub-processors, all
          under written data processing agreements:
        </BodyText>

        {[
          {
            name: "Supabase",
            role: "Database, authentication, and file storage",
            cert: "SOC 2 Type II",
            note: "All project data, user accounts, and session tokens are stored in Supabase on AWS us-east-1.",
          },
          {
            name: "Vercel",
            role: "Frontend hosting and serverless API",
            cert: "SOC 2 Type II",
            note: "The application UI and API routes are served from Vercel's global edge network.",
          },
          {
            name: "Anthropic",
            role: "AI-assisted estimate import (optional feature)",
            cert: "SOC 2 Type II",
            note: "Used only when you explicitly trigger the AI import feature. Estimate data sent to Anthropic is not used to train their models per their zero-data-retention API agreement.",
          },
          {
            name: "Stripe",
            role: "Subscription billing and payment processing",
            cert: "PCI DSS Level 1",
            note: "Handles all payment card data. We receive only a Stripe customer ID and subscription status.",
          },
          {
            name: "Resend",
            role: "Transactional email delivery",
            cert: "SOC 2 Type II",
            note: "Sends account confirmations, password resets, and subscription receipts. We pass only your email address and the message content to Resend.",
          },
        ].map(({ name, role, cert, note }) => (
          <div key={name} style={{
            background: PANEL, border: `1px solid ${BORDER}`,
            borderRadius: 8, padding: "16px 20px", marginBottom: 12,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8, marginBottom: 6 }}>
              <div>
                <span style={{ fontWeight: 700, color: TEXT, fontSize: 14 }}>{name}</span>
                <span style={{ fontSize: 13, color: MUTED, marginLeft: 10 }}>{role}</span>
              </div>
              <span style={{
                fontSize: 11, fontWeight: 600, color: TEAL,
                background: TEAL + "18", border: `1px solid ${TEAL}40`,
                borderRadius: 999, padding: "2px 10px", whiteSpace: "nowrap",
              }}>
                {cert}
              </span>
            </div>
            <p style={{ fontSize: 13, color: MUTED, margin: 0, lineHeight: 1.6 }}>{note}</p>
          </div>
        ))}

        <Divider />

        {/* 6. Data Retention */}
        <SectionHeading>6. Data Retention</SectionHeading>
        <BodyText>
          We retain your data for as long as your account is active. When you request account
          deletion (via <Term>Settings → Privacy &amp; Data → Delete Account</Term>), we permanently
          delete your profile, all associated projects, snapshots, and your authentication record
          from our systems within <Term>30 days</Term>. This is not a soft delete — the data is
          permanently purged.
        </BodyText>
        <BodyText>
          IP address audit logs are retained for <Term>90 days</Term> and then automatically purged,
          regardless of account status.
        </BodyText>
        <BodyText>
          If your subscription lapses without cancellation, your project data is retained for{" "}
          <Term>30 days</Term> in a read-only state to allow re-activation or export, then
          permanently deleted.
        </BodyText>

        <Divider />

        {/* 7. Your Rights (GDPR) */}
        <SectionHeading>7. Your Rights Under GDPR</SectionHeading>
        <BodyText>
          If you are in the EEA or UK, you have the following rights regarding your personal data.
          All self-service controls are available in <Term>Settings → Privacy &amp; Data</Term>.
          For requests that cannot be fulfilled self-service, contact{" "}
          <a href="mailto:privacy@open-estimate.com" style={{ color: TEAL, textDecoration: "none" }}>
            privacy@open-estimate.com
          </a>{" "}and we will respond within <Term>30 days</Term>.
        </BodyText>
        <BulletList items={[
          <><Term>Right of access (Art. 15):</Term> Request a copy of all personal data we hold about you.</>,
          <><Term>Right to rectification (Art. 16):</Term> Correct inaccurate personal data — your display name and email can be updated directly in Settings.</>,
          <><Term>Right to erasure (Art. 17):</Term> Request permanent deletion of your account and all associated data via Settings → Privacy &amp; Data → Delete Account.</>,
          <><Term>Right to data portability (Art. 20):</Term> Export a complete JSON archive of your profile and all project data at any time — Settings → Privacy &amp; Data → Export Data.</>,
          <><Term>Right to restriction (Art. 18):</Term> Request that we restrict processing of your data while a dispute is resolved.</>,
          <><Term>Right to object (Art. 21):</Term> Object to processing based on legitimate interests. We will cease processing unless we have compelling grounds to continue.</>,
          <><Term>Right to lodge a complaint:</Term> You have the right to lodge a complaint with your local data protection authority (e.g., ICO in the UK, or your national DPA in the EU).</>,
        ]} />

        <Divider />

        {/* 8. CCPA */}
        <SectionHeading>8. California Privacy Rights (CCPA/CPRA)</SectionHeading>
        <BodyText>
          If you are a California resident, you have the following rights under the California
          Consumer Privacy Act (CCPA) as amended by the CPRA:
        </BodyText>
        <BulletList items={[
          <><Term>Right to know:</Term> You may request disclosure of the categories and specific pieces of personal information we have collected about you in the past 12 months.</>,
          <><Term>Right to delete:</Term> You may request deletion of personal information we have collected about you, subject to certain exceptions.</>,
          <><Term>Right to opt out of sale:</Term> <Term>We do not sell your personal information</Term> and have not done so in the past 12 months. You therefore have no sale to opt out of.</>,
          <><Term>Right to non-discrimination:</Term> We will not discriminate against you for exercising any of your CCPA rights.</>,
        ]} />
        <BodyText>
          To exercise CCPA rights, contact{" "}
          <a href="mailto:privacy@open-estimate.com" style={{ color: TEAL, textDecoration: "none" }}>
            privacy@open-estimate.com
          </a>{" "}with the subject line "CCPA Request."
        </BodyText>

        <Divider />

        {/* 9. Cookies */}
        <SectionHeading>9. Cookies and Session Storage</SectionHeading>
        <BodyText>
          OpenEstimate does <Term>not</Term> use persistent tracking cookies or advertising cookies.
        </BodyText>
        <BodyText>
          We use <Term>sessionStorage</Term> (browser memory cleared when the tab closes) to hold
          your authentication token during an active session. This is a security measure — auth
          tokens stored in sessionStorage are inaccessible to other browser tabs and are
          automatically discarded when you close the tab.
        </BodyText>
        <BodyText>
          We do not use Google Analytics, Facebook Pixel, or any third-party tracking scripts.
          If we add analytics tooling in the future, it will be disclosed here and, where required,
          will require your consent.
        </BodyText>

        <Divider />

        {/* 10. Children */}
        <SectionHeading>10. Children's Privacy</SectionHeading>
        <BodyText>
          OpenEstimate is a professional B2B tool intended for use by adults in the construction
          industry. <Term>We do not knowingly collect personal data from anyone under 13 years of age.</Term>{" "}
          If we become aware that a user under 13 has created an account, we will permanently
          delete the account and all associated data immediately. If you believe a child has
          registered, please contact us at{" "}
          <a href="mailto:privacy@open-estimate.com" style={{ color: TEAL, textDecoration: "none" }}>
            privacy@open-estimate.com
          </a>.
        </BodyText>

        <Divider />

        {/* 11. International Transfers */}
        <SectionHeading>11. International Data Transfers</SectionHeading>
        <BodyText>
          Your data is stored in the <Term>United States</Term> (AWS us-east-1 via Supabase). If
          you access OpenEstimate from the EEA, UK, or other regions, your data is transferred to
          and processed in the United States. For EEA and UK users, this transfer is conducted
          under the EU Standard Contractual Clauses (SCCs) with our sub-processors.
        </BodyText>

        <Divider />

        {/* 12. Changes */}
        <SectionHeading>12. Changes to This Policy</SectionHeading>
        <BodyText>
          We may update this Privacy Policy from time to time. For <Term>material changes</Term>{" "}
          (changes that meaningfully affect how we collect, use, or share your data), we will notify
          you by email to the address on your account at least <Term>30 days before</Term> the
          changes take effect. Continued use of the service after the effective date constitutes
          acceptance of the updated policy.
        </BodyText>
        <BodyText>
          Non-material changes (e.g., clarifications, formatting, fixing typos) may be made without
          advance notice. The "Last updated" date at the top of this page always reflects the most
          recent revision.
        </BodyText>

        <Divider />

        {/* 13. DPA */}
        <SectionHeading>13. Data Processing Agreements</SectionHeading>
        <BodyText>
          Enterprise customers who require a signed <Term>Data Processing Agreement (DPA)</Term>{" "}
          for GDPR compliance may request one at{" "}
          <a href="mailto:privacy@open-estimate.com" style={{ color: TEAL, textDecoration: "none" }}>
            privacy@open-estimate.com
          </a>. We will execute a DPA incorporating the EU Standard Contractual Clauses (SCCs)
          as applicable.
        </BodyText>

        <Divider />

        {/* Contact */}
        <div style={{
          background: TEAL + "0d", border: `1px solid ${TEAL}30`,
          borderRadius: 10, padding: "28px 32px",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          flexWrap: "wrap", gap: 20,
        }}>
          <div>
            <h3 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700, color: TEXT }}>
              Privacy questions or requests?
            </h3>
            <p style={{ margin: 0, fontSize: 13, color: MUTED, maxWidth: 480, lineHeight: 1.6 }}>
              For any privacy-related questions, GDPR/CCPA requests, or to request a Data
              Processing Agreement, contact us. We respond within 2 business days.
            </p>
          </div>
          <a
            href="mailto:privacy@open-estimate.com"
            style={{
              display: "inline-block", background: TEAL, color: BG,
              fontWeight: 700, fontSize: 13, textDecoration: "none",
              padding: "10px 22px", borderRadius: 6, whiteSpace: "nowrap",
              fontFamily: FONT,
            }}
          >
            privacy@open-estimate.com
          </a>
        </div>

        <p style={{ fontSize: 12, color: MUTED, textAlign: "center", marginTop: 40, opacity: 0.6 }}>
          Last updated April 2026 · OpenEstimate is operated by Alex Bruce ·{" "}
          <a href="mailto:privacy@open-estimate.com" style={{ color: TEAL, textDecoration: "none" }}>
            privacy@open-estimate.com
          </a>
        </p>

      </div>
    </div>
  );
}
